import { useState } from "react";
import SectionHead from "./SectionHead";
import { comments, heatWords } from "@/mocks/social";
import type { CommentItem } from "@/mocks/social";

const sortTabs = ["高赞", "最新", "最有争议"];

function fmt(n: number) {
  return n.toLocaleString("zh-CN");
}

export default function Community() {
  const [sort, setSort] = useState("高赞");
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [stand, setStand] = useState("");
  const [posted, setPosted] = useState(false);

  return (
    <section id="community" className="scroll-mt-24 bg-background-100">
      <div className="mx-auto w-full max-w-[1240px] px-4 py-10 md:px-6 md:py-16">
        <SectionHead
          no="06"
          kicker="讨论区 · 高赞观点"
          title="评论区比正片更精彩"
          sub="按赞数与观点质量聚合社区高赞讨论，只精选有理有据、而非情绪刷屏的声音。"
        />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap gap-2">
                {heatWords.map((w) => (
                  <span
                    key={w}
                    className="whitespace-nowrap rounded-full bg-background-50 px-3 py-1 text-xs font-medium text-foreground-700 ring-1 ring-secondary-200"
                  >
                    #{w}
                  </span>
                ))}
              </div>
              <div className="flex shrink-0 items-center gap-1 rounded-full bg-background-50 p-1 ring-1 ring-secondary-200">
                {sortTabs.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setSort(t)}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                      sort === t ? "bg-foreground-950 text-background-50" : "text-secondary-600 hover:text-foreground-800"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              {comments.map((c) => {
                const isLiked = !!liked[c.user];
                const count = c.vote + (isLiked ? 1 : 0);
                return (
                  <article
                    key={c.user}
                    className="rounded-lg border border-secondary-200 bg-background-50 p-4 md:p-5"
                  >
                    <div className="flex items-center gap-3">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary-100 font-heading text-sm font-bold text-primary-700">
                        {c.user.charAt(0)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-foreground-950">{c.user}</p>
                        <p className="text-[11px] text-secondary-500">{c.tag}</p>
                      </div>
                      <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-accent-700">
                        <i className="ri-fire-fill" />
                        {fmt(count)} 赞同
                      </span>
                    </div>

                    <p className="mt-3 text-sm leading-7 text-foreground-800">{c.text}</p>

                    <div className="mt-3 flex items-center gap-2 border-t border-secondary-200 pt-3 text-[11px] text-secondary-500">
                      <button
                        type="button"
                        onClick={() => setLiked((p) => ({ ...p, [c.user]: !p[c.user] }))}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-semibold transition-colors ${
                          isLiked
                            ? "bg-primary-100 text-primary-700"
                            : "bg-background-100 text-secondary-600 hover:text-primary-600"
                        }`}
                      >
                        <i className={isLiked ? "ri-thumb-up-fill" : "ri-thumb-up-line"} />
                        {isLiked ? "已赞同" : "赞同"}
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-full bg-background-100 px-3 py-1.5 font-semibold transition-colors hover:text-foreground-800"
                      >
                        <i className="ri-chat-forward-line" />
                        跟评
                      </button>
                      <span className="ml-auto inline-flex items-center gap-1">
                        <i className="ri-eye-line" />
                        已围观 4.2万
                      </span>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          <div className="space-y-5">
            <div className="rounded-lg border border-secondary-200 bg-background-50 p-4 md:p-5">
              <p className="flex items-center gap-2 text-sm font-bold text-foreground-950">
                <i className="ri-coupon-3-line text-accent-600" />
                这瓜你站哪边？
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {["站爆料方", "站节目组", "中立吃瓜", "还没想好"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStand(s)}
                    className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                      stand === s
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-secondary-600 hover:text-foreground-800"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <p className="mt-3 flex items-center gap-2 border-t border-secondary-200 pt-3 text-[11px] text-secondary-500">
                <i className="ri-user-voice-line" />
                {stand ? `已选「${stand}」` : "尚未表态"} · 2.4万 已表态（演示）
              </p>
            </div>

            <div className="rounded-lg border border-secondary-200 bg-background-50 p-4 md:p-5">
              <p className="mb-3 flex items-center gap-2 text-sm font-bold text-foreground-950">
                <i className="ri-edit-2-line text-primary-600" />
                加入讨论
              </p>
              <textarea
                aria-label="观点"
                placeholder="说说你的看法，最好带证据……"
                className="h-24 w-full resize-none rounded-md border border-secondary-200 bg-background-100 p-3 text-sm text-foreground-800 outline-none focus:border-primary-400"
              />
              <div className="mt-3 flex items-center justify-between">
                <span className="text-[11px] text-secondary-500">0 / 500</span>
                <button
                  type="button"
                  onClick={() => setPosted(true)}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  发表观点
                  <i className="ri-send-plane-line text-sm" />
                </button>
              </div>
              {posted && (
                <p className="mt-3 rounded-md bg-secondary-100 px-3 py-2 text-xs text-secondary-800">
                  已收录你的观点（演示站点，内容不对外公开）
                </p>
              )}
            </div>

            <div className="rounded-lg border border-secondary-200 bg-background-50 p-4 md:p-5">
              <p className="mb-2 text-xs font-bold tracking-widest text-foreground-800">本期讨论标签</p>
              <div className="flex flex-wrap gap-1.5">
                {["实锤党", "反方", "现场党", "理性围观", "路人", "吃瓜群众"].map((t) => (
                  <span
                    key={t}
                    className="whitespace-nowrap rounded bg-background-100 px-2 py-1 text-[11px] text-secondary-600"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}